import {
  setAgentDispatcherResolver,
  type AgentDispatcher,
} from '@tunasong/redux/api/agent-client-tool-registry'
import { useEffect, useRef } from 'react'
import type { ClientToolContext } from './client-tools/context.js'
import { createClientToolDispatcher } from './client-tool-dispatcher.js'

/**
 * Module-scope map of agentId → dispatcher. The resolver in lib/redux is a
 * single function; we own the lookup table here and replace the resolver
 * once (on first hook mount) to point at our map. Subsequent hook mounts
 * just write to the map.
 *
 * Cleared on unmount to prevent stale dispatchers from firing against
 * remounted agents (e.g. HMR, sidebar tab switch).
 */
const dispatchersByAgent = new Map<string, AgentDispatcher & { reset?: () => void }>()

/**
 * Install (or re-install) our lookup function as the active resolver.
 * The registry holds a single resolver slot; calling setAgentDispatcherResolver
 * again simply replaces it, which is safe. We re-install on every useEffect
 * run so that test helpers that call clearAgentDispatcherResolver() between
 * tests don't leave subsequent renders without a resolver.
 */
const installResolver = (): void => {
  setAgentDispatcherResolver(agentId => dispatchersByAgent.get(agentId))
}

const buildDispatcher = (
  getContextRef: React.RefObject<(() => Omit<ClientToolContext, 'callsThisTurn'>) | null>
): AgentDispatcher & { reset: () => void } => {
  const base = createClientToolDispatcher(() => ({
    ...(getContextRef.current?.() ?? { editor: null, plugins: [], audioEngine: null }),
    callsThisTurn: 0,
  }))
  // Adapt our internal ClientToolDispatcher to the registry's AgentDispatcher
  // shape, and carry `.reset` so resetCounter can reach it.
  const adapter = (call: Parameters<AgentDispatcher>[0]) =>
    base({
      toolName: call.toolName,
      toolCallId: call.toolCallId,
      input: call.input,
    })
  return Object.assign(adapter, { reset: base.reset.bind(base) })
}

export interface UseClientToolHostResult {
  /**
   * Reset the per-turn rate-limit counter for this agent's dispatcher.
   * Call on each new user-initiated submit so the 8-call budget is fresh.
   */
  resetCounter: () => void
}

/**
 * Owns the client-tool dispatcher for a given agent thread. The hook:
 * 1. Constructs a dispatcher bound to the supplied `getContext` callback
 *    (called per dispatch so the executor sees fresh editor / audio refs).
 * 2. Registers the dispatcher in a module-scope map keyed by agentId so
 *    the agentStream RTK handler can look it up.
 * 3. Unregisters on unmount.
 *
 * Returns `resetCounter` so AgentChat can flush the per-turn rate limit on
 * each new submit (the dispatcher counter is per-turn, not per-call).
 */
export const useClientToolHost = (
  agentId: string,
  getContext: () => Omit<ClientToolContext, 'callsThisTurn'>
): UseClientToolHostResult => {
  // Store the latest getContext in a ref so the dispatcher closure always
  // reads the live value without needing to recreate the dispatcher on every
  // render. The dispatcher is stable per agentId — recreating it would lose
  // the per-turn counter mid-turn.
  const getContextRef = useRef<(() => Omit<ClientToolContext, 'callsThisTurn'>) | null>(null)
  getContextRef.current = getContext

  // Keep a stable dispatcher per agentId. We recreate when agentId changes
  // so each new agent thread gets a fresh per-turn rate-limit counter.
  const dispatcherRef = useRef<(AgentDispatcher & { reset: () => void }) | null>(null)
  const prevAgentIdRef = useRef<string | null>(null)
  if (prevAgentIdRef.current !== agentId) {
    prevAgentIdRef.current = agentId
    dispatcherRef.current = buildDispatcher(getContextRef)
  }
  const dispatcher = dispatcherRef.current as AgentDispatcher & { reset: () => void }

  useEffect(() => {
    installResolver()
    dispatchersByAgent.set(agentId, dispatcher)
    return () => {
      dispatchersByAgent.delete(agentId)
    }
  }, [agentId, dispatcher])

  return {
    resetCounter: () => {
      // Reach into the Map directly (same source as the resolver uses) so
      // we can call the augmented `.reset` method that AgentDispatcher itself
      // doesn't declare.
      const live = dispatchersByAgent.get(agentId)
      live?.reset?.()
    },
  }
}
